// [ai] The import flow's state: the drop overlay, the sheet (offer → progress), what is pending (files, fields, dupes, the
// user's unchecks), the upload snapshot the progress view paints, background mode + the bar pill, the last commit, the E2E
// counters. One vanilla store created by the Importer controller (imports/importer.ts); components read it through useImports().
import { createStore, useStore, type StoreApi } from "zustand";
import { createContext, useContext } from "react";
import type { Plan } from "../imports/plan";
import type { Committed, UpFile } from "../imports/upload";

export type SheetMode = "closed" | "offer" | "progress";
export type Speakers = "solo" | "multi";
export type Pending = { raw: UpFile[]; method: string; title: string; tags: string; note: string; speakers: Speakers; dupes: Record<string, string>; hashes: Record<string, string>; error: string | null; planned: Plan; unchecked: string[]; touched: string[] };
export type FileRow = { path: string; sent: number; size: number; state: "waiting" | "sending" | "sent" };
export type Progress = { phase: "idle" | "uploading" | "committing" | "done" | "error" | "cancelled"; sent: number; total: number; error: string | null; files: FileRow[]; eta: number | null };
export type Upload = { title: string; files: { path: string; size: number; icon: string }[] };
export type ImportsState = {
  overlay: boolean; sheet: SheetMode; pending: Pending | null; upload: Upload | null; progress: Progress | null; bg: boolean; last: Committed | null; resumed: string | null; stats: { drops: number; pastes: number }; armed: boolean;
  set: StoreApi<ImportsState>["setState"];
};
export function createImportsStore(): StoreApi<ImportsState> {
  return createStore<ImportsState>(set => ({ overlay: false, sheet: "closed", pending: null, upload: null, progress: null, bg: false, last: null, resumed: null, stats: { drops: 0, pastes: 0 }, armed: false, set }));
}
export const ImportsStoreContext = createContext<StoreApi<ImportsState> | null>(null);
export function useImports<T>(selector: (s: ImportsState) => T): T {
  const s = useContext(ImportsStoreContext); if (!s) throw new Error("useImports outside <App>: the imports store is provided by App");
  return useStore(s, selector);
}
/** The pill's text while an upload runs in the background (null when there is nothing to show). */
export function pillText(s: ImportsState): string | null {
  if (!s.bg || !s.progress || !["uploading", "committing"].includes(s.progress.phase)) return null;
  const pct = s.progress.total ? Math.round(100 * s.progress.sent / s.progress.total) : 0;
  return s.progress.phase === "committing" ? "Filing…" : `Importing ${pct}%`;
}
